import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';
import {
  updateCollection,
  UpdateCollectionResponse,
} from '@/services/data-sources/update-collection';
import {
  CollectionSourceConfig,
  CollectionSource,
  ExternalChatFilterDef,
} from '@/types/source-types';
import { collectionQueryKey } from '@/hooks/queries/data-sources/use-get-collection';

export interface UpdateCollectionVariables {
  id: string;
  name?: string;
  description?: string;
  source?: CollectionSource;
  sourceConfig?: CollectionSourceConfig | null;
  config?: Record<string, unknown>;
  exposedUploadAttributes?: string[] | null;
  exposedUploadAttributeCardinality?: Record<string, 'single' | 'multi'> | null;
  externalChatFilters?: ExternalChatFilterDef[] | null;
}

interface Options
  extends Omit<
    UseMutationOptions<
      UpdateCollectionResponse,
      Error,
      UpdateCollectionVariables
    >,
    'mutationFn'
  > {
  orgId?: string;
}

export const useUpdateCollection = (options?: Options) => {
  const queryClient = useQueryClient();
  const { orgId, ...mutationOptions } = options || {};

  return useMutation<
    UpdateCollectionResponse,
    Error,
    UpdateCollectionVariables
  >({
    mutationFn: async (vars) => {
      const response = await updateCollection(vars);
      // updateCollection resolves with { detail } on validation/server/network
      // errors instead of throwing — normalize to a thrown Error so callers
      // (and the mutation's onError) see a rejected promise.
      if (response?.detail) {
        throw new Error(response.detail);
      }
      return response;
    },
    onSuccess: async (_data, vars) => {
      // The PATCH response does not include the updated collection, so refetch
      // the authoritative record. Awaiting here makes mutateAsync wait for the
      // refetch, so a caller that rebuilds from config after saving reads fresh
      // data — replacing the manual fetchCollection race guards.
      await queryClient.invalidateQueries({
        queryKey: collectionQueryKey(orgId, vars.id),
      });
    },
    ...mutationOptions,
  });
};
